import { InputNumber, Segmented } from 'antd';

import { FILE_PLATFORM_MAX_MB } from '../../utils/fileValues';

/**
 * The card-definition sub-editor for a `file` element's own options
 * (docs/plans/data-types-file-element.md §2; the 10b contract):
 *
 *   accept      'any' (the default, written as ABSENT) or 'image' — "Photos
 *               only" means the server's own check found a photo (JPEG, PNG,
 *               WebP, GIF, AVIF, TIFF, HEIC); a PDF is never one.
 *   maxSizeMb   a whole number from 1 to 100; blank is the platform's 100.
 *               Lowering it keeps the files already saved (a size is a cost
 *               limit, not the shape of the value).
 *
 * "Allow multiple" is the toggle row beside the others (its switch asks the
 * conversion dialog over stored values, utils/shapeChange.js). The API
 * checks both rules again at the upload and on every save.
 */

export const FILE_ACCEPT_OPTIONS = [
	{ value: 'any', label: 'Any file' },
	{ value: 'image', label: 'Photos only' },
];

export default function FileOptions({ index, element, onChange, disabled = false, basePath = null }) {
	const options = element?.options || {};
	const prefix = `${basePath || `elements[${index}]`}.options`;
	const accept = options.accept === 'image' ? 'image' : 'any';
	const maxSizeMb = Number.isInteger(options.maxSizeMb) ? options.maxSizeMb : null;

	return (
		<div className='file-options' data-testid='file-options'>
			<div className='micro-label card-def-element__section'>File</div>
			<div className='mb-2'>
				<label className='d-block' htmlFor={`${prefix}.accept`}>Accept</label>
				<Segmented
					id={`${prefix}.accept`}
					options={FILE_ACCEPT_OPTIONS}
					value={accept}
					disabled={disabled}
					onChange={next => onChange(`${prefix}.accept`, next === 'image' ? 'image' : undefined)}
					aria-label='Accept'
				/>
				<div className='form-panel__hint'>
					Photos only takes the pictures keepr can show — a PDF or a document is refused.
				</div>
			</div>
			<div className='mb-2'>
				<label className='d-block' htmlFor={`${prefix}.maxSizeMb`}>Max size</label>
				<InputNumber
					id={`${prefix}.maxSizeMb`}
					min={1}
					max={FILE_PLATFORM_MAX_MB}
					step={1}
					precision={0}
					value={maxSizeMb}
					placeholder={String(FILE_PLATFORM_MAX_MB)}
					addonAfter='MB'
					disabled={disabled}
					aria-label='Max size in MB'
					onChange={(next) => {
						const whole = Number.isFinite(next) ? Math.round(next) : null;
						const fits = whole !== null && whole >= 1 && whole <= FILE_PLATFORM_MAX_MB;
						onChange(`${prefix}.maxSizeMb`, fits ? whole : undefined);
					}}
				/>
				<div className='form-panel__hint'>
					{`Leave blank for keepr's limit of ${FILE_PLATFORM_MAX_MB} MB. Lowering it keeps the files already saved.`}
				</div>
			</div>
		</div>
	);
}
